import React, { useState, useEffect, useRef, useCallback } from 'react'
import * as XLSX from 'xlsx'
import apiService from '../../services/api.service'
import { LoadingOverlay } from '../../components/loading'
import './movimientos.css'

interface Finca {
  _id: string
  nombre: string
  codigo: string
  tipos: string[]
  imagen: string
  imagen_url: string
}

interface FinancialGroup {
  id?: string
  nombre: string
  orden: number
  activo: boolean
  created_at: string
}

interface FinancialSubcategory {
  nombre: string
  id_padre: string
  grupo: string
  codigos: Record<string, string>
  activo: boolean
}

const STORAGE_KEY = 'pilotoGanando.movements.v1'
const DEFAULT_ROW_COUNT = 10

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
]

/** Genera años para el selector (ej. 2020..2030) */
function getYears(): number[] {
  const current = new Date().getFullYear()
  const from = current - 5
  const to = current + 1
  return Array.from({ length: to - from + 1 }, (_, i) => from + i)
}

/** Convierte fecha ISO o dd/mm/aaaa a dd/mm/aaaa para el formulario */
function normalizeDateForRow(value: string | undefined): string {
  if (!value || typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) return trimmed
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch
    return `${dd}/${mm}/${yyyy}`
  }
  const d = new Date(trimmed)
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = d.getFullYear()
    return `${dd}/${mm}/${yyyy}`
  }
  return ''
}

export interface RowData {
  id: string
  date: string
  farm: string
  category: string
  subcategory: string
  description: string
  value: string
}

function createEmptyRow(): RowData {
  return {
    id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    date: '',
    farm: '',
    category: '',
    subcategory: '',
    description: '',
    value: '',
  }
}

function loadFromLocalStorage(): RowData[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.map((item: Partial<RowData>) => ({
      ...createEmptyRow(),
      ...item,
      id: item.id || createEmptyRow().id,
    }))
  } catch {
    return []
  }
}

function saveToLocalStorage(rows: RowData[]) {
  const payload = rows.map(({ id, ...rest }) => rest)
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
}

/** Formatea fecha mientras se escribe al formato dd/mm/aaaa */
function formatDateInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8)
  const len = digits.length
  if (len <= 2) return digits
  if (len <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

/** Valida que una fecha dd/mm/aaaa exista en el calendario */
function isValidDateString(value: string): boolean {
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return false
  const [dd, mm, yyyy] = value.split('/')
  const day = Number(dd)
  const month = Number(mm)
  const year = Number(yyyy)
  if (year < 1900 || year > 3000) return false
  if (month < 1 || month > 12) return false
  const date = new Date(year, month - 1, day)
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  )
}

/** Convierte fecha dd/mm/aaaa a YYYY-MM-DD para el API */
function dateToApiFormat(value: string): string {
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return ''
  const [dd, mm, yyyy] = value.split('/')
  return `${yyyy}-${mm}-${dd}`
}

const FARM_VALUE_SEP = '|'

/** Opciones para el select de finca: si tiene tipos, solo "nombre - tipo"; si no tiene, solo "nombre". */
function getFarmOptions(farms: Finca[]): { value: string; label: string; key: string }[] {
  const options: { value: string; label: string; key: string }[] = []
  for (const finca of farms) {
    if (finca.tipos?.length) {
      for (const tipo of finca.tipos) {
        options.push({
          value: `${finca.nombre}${FARM_VALUE_SEP}${tipo}`,
          label: `${finca.nombre} - ${tipo}`,
          key: `${finca._id}-${tipo}`,
        })
      }
    } else {
      options.push({
        value: `${finca.nombre}${FARM_VALUE_SEP}`,
        label: finca.nombre,
        key: `${finca._id}-`,
      })
    }
  }
  return options
}

/** Parsea value del select (nombre|tipo) a { finca, tipo } para el API. */
function parseFarmValue(farmValue: string): { finca: string; tipo: string } {
  const idx = farmValue.indexOf(FARM_VALUE_SEP)
  if (idx === -1) return { finca: farmValue.trim(), tipo: '' }
  return {
    finca: farmValue.slice(0, idx).trim(),
    tipo: farmValue.slice(idx + 1).trim(),
  }
}

/** Formatea un número para mostrar: $ 10'000.000 (apóstrofe miles, punto antes de unidades; decimales con coma) */
function formatCurrencyDisplay(value: string): string {
  const trimmed = value.trim().replace(/,/g, '.')
  if (!trimmed || trimmed === '.') return ''
  const num = parseFloat(trimmed)
  if (Number.isNaN(num)) return value
  const [intPart, decPart] = trimmed.split('.')
  const intStr = intPart.replace(/\D/g, '') || '0'
  const groups: string[] = []
  for (let i = intStr.length; i > 0; i -= 3) {
    groups.unshift(intStr.slice(Math.max(0, i - 3), i))
  }
  let formatted =
    groups.length <= 1
      ? groups[0] || '0'
      : groups.length === 2
        ? `${groups[0]}.${groups[1]}`
        : groups.slice(0, -2).join("'") + "'" + groups[groups.length - 2] + '.' + groups[groups.length - 1]
  if (decPart !== undefined && decPart !== '') {
    formatted += ',' + decPart.replace(/\D/g, '').slice(0, 2)
  }
  return formatted
}

/** Parsea valor (formateado 10'000.000 o raw 10000000) a número string para guardar */
function parseCurrencyInput(value: string): string {
  const trimmed = value.trim().replace(/,/g, '.')
  if (!trimmed || trimmed === '.') return ''
  const noApostrophe = trimmed.replace(/'/g, '')
  const parts = noApostrophe.split('.')
  const isDecimal = parts.length === 2 && parts[1].length <= 2 && /^\d+$/.test(parts[1])
  const numStr = isDecimal
    ? parts[0].replace(/\D/g, '') + '.' + parts[1]
    : parts.join('').replace(/\D/g, '')
  if (!numStr || numStr === '.') return ''
  const num = parseFloat(numStr)
  if (Number.isNaN(num) || num < 0) return ''
  return num.toString()
}

const now = new Date()
const initialLoadMonth = now.getMonth() + 1
const initialLoadYear = now.getFullYear()

const Movimientos: React.FC = () => {
  const [rows, setRows] = useState<RowData[]>([])
  const [addRowCount, setAddRowCount] = useState<string>('')
  const [farms, setFarms] = useState<Finca[]>([])
  const [loadFarmFilter, setLoadFarmFilter] = useState<string>('')
  const [loadFarmPickerOpen, setLoadFarmPickerOpen] = useState(false)
  const [openSheetSelect, setOpenSheetSelect] = useState<{
    rowIndex: number
    col: 'farm' | 'category' | 'subcategory'
  } | null>(null)
  const [groups, setGroups] = useState<FinancialGroup[]>([])
  const [subcategories, setSubcategories] = useState<FinancialSubcategory[]>([])
  const [showConfirmClear, setShowConfirmClear] = useState(false)
  const [fillHighlight, setFillHighlight] = useState<{ start: number; end: number; col: number } | null>(null)
  const [hoveredDescriptionRow, setHoveredDescriptionRow] = useState<number | null>(null)
  const [loadMonth, setLoadMonth] = useState(initialLoadMonth)
  const [loadYear, setLoadYear] = useState(initialLoadYear)
  const [loadFromDbLoading, setLoadFromDbLoading] = useState(false)
  const [loadFromDbError, setLoadFromDbError] = useState<string | null>(null)
  const [loadFromDbPickerOpen, setLoadFromDbPickerOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const loadFromDbPickerRef = useRef<HTMLDivElement>(null)
  const tableBodyRef = useRef<HTMLTableSectionElement>(null)
  const fillDragRef = useRef<{
    colIndex: number
    startRowIndex: number
    endRowIndex: number
    value: string
    sourceCategory: string
  } | null>(null)

  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const [fincasRes, groupsRes] = await Promise.all([
          apiService.get<{ fincas: Finca[]; total: number }>('/api/fincas'),
          apiService.get<{ groups: FinancialGroup[]; total: number }>('/api/financial-groups'),
        ])

        setFarms(fincasRes.data.fincas || [])
        const groupsList = groupsRes.data.groups || []
        setGroups(groupsList)

        const groupsWithId = groupsList.filter((g) => g.id ?? (g as any)?.id_padre)
        if (groupsWithId.length === 0) {
          setSubcategories([])
        } else {
          const subcatResponses = await Promise.all(
            groupsWithId.map((g) => {
              const pid = g.id ?? (g as any)?.id_padre
              return apiService.get<{
                subcategories: FinancialSubcategory[]
                total: number
                grupo_filtro?: string | null
                id_padre_filtro?: string | null
              }>(`/api/financial-subcategories?id_padre=${encodeURIComponent(pid)}`)
            })
          )
          const merged = subcatResponses.flatMap((r) => r.data.subcategories || [])
          setSubcategories(merged)
        }
      } catch (error) {
        console.error('Error cargando datos de referencia para movimientos:', error)
      }
    }

    fetchReferenceData()

    const loaded = loadFromLocalStorage()
    if (loaded.length > 0) {
      setRows(loaded)
    } else {
      const initial = Array.from({ length: DEFAULT_ROW_COUNT }, () => createEmptyRow())
      setRows(initial)
      saveToLocalStorage(initial)
    }
  }, [])

  useEffect(() => {
    if (rows.length === 0) return
    saveToLocalStorage(rows)
  }, [rows])

  const getSubcategories = useCallback(
    (category: string): string[] => {
      if (!category) return []
      return subcategories
        .filter((s) => s.grupo === category && s.activo)
        .map((s) => s.nombre)
    },
    [subcategories]
  )

  const updateRow = useCallback((index: number, updates: Partial<RowData>) => {
    setRows((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], ...updates }
      if (updates.category !== undefined) {
        next[index].subcategory = ''
      }
      return next
    })
  }, [])

  const addRow = useCallback((count: number = 1) => {
    const safeCount = Number.isFinite(count) && count > 0 ? Math.floor(count) : 1
    setRows((prev) => [
      ...prev,
      ...Array.from({ length: safeCount }, () => createEmptyRow()),
    ])
  }, [])

  const removeRow = useCallback((index: number) => {
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== index)
      if (next.length === 0) next.push(createEmptyRow())
      return next
    })
  }, [])

  const handleExport = useCallback(() => {
    const headers = ['Fecha', 'Finca', 'Categoría', 'Subcategoría', 'Descripción', 'Valor']
    const nonEmpty = rows.filter(
      (r) => r.date || r.farm || r.category || r.subcategory || r.description || r.value
    )
    const worksheetRows = [
      headers,
      ...nonEmpty.map((r) => [r.date, r.farm, r.category, r.subcategory, r.description, r.value]),
    ]
    const ws = XLSX.utils.aoa_to_sheet(worksheetRows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Movimientos')
    const now = new Date()
    const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    XLSX.writeFile(wb, `movimientos-${stamp}.xlsx`)
  }, [rows])

  const handleGuardar = useCallback(async () => {
    setIsSaving(true)
    const movimientos = rows
      .filter(
        (r) =>
          r.date &&
          r.farm &&
          r.category &&
          r.subcategory &&
          r.description &&
          r.value &&
          isValidDateString(r.date)
      )
      .map((r) => {
        const { finca, tipo } = parseFarmValue(r.farm)
        return {
          finca,
          tipo,
          grupo: r.category.trim(),
          subcategoria: r.subcategory.trim(),
          descripcion: r.description.trim(),
          valor: Number(r.value),
          fecha: dateToApiFormat(r.date),
        }
      })
    handleExport()
    if (movimientos.length > 0) {
      try {
        await apiService.post('/api/financial-manual', { movimientos })
      } catch {
        // El Excel ya se generó; el usuario puede revisar el error en consola o añadir notificación
      }
    }
    setIsSaving(false)
  }, [rows, handleExport])

  const handleClearForm = useCallback(() => setShowConfirmClear(true), [])
  const handleCancelClear = useCallback(() => setShowConfirmClear(false), [])
  const handleConfirmClear = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
    setRows(Array.from({ length: DEFAULT_ROW_COUNT }, () => createEmptyRow()))
    setShowConfirmClear(false)
  }, [])

  const handleLoadFromDb = useCallback(async () => {
    setLoadFromDbError(null)

    if (!loadFarmFilter) {
      setLoadFromDbError('Debes seleccionar una finca y tipo antes de cargar.')
      return
    }

    const { finca, tipo } = parseFarmValue(loadFarmFilter)
    if (!finca || !tipo) {
      setLoadFromDbError('La finca seleccionada no tiene un tipo válido.')
      return
    }

    setLoadFromDbLoading(true)
    try {
      const { data } = await apiService.get<{
        finca: string
        tipo: string
        fecha_str: string
        fecha: string
        total_costos: number
        total_gastos: number
        total_ingresos: number
        total_movimientos: number
        movimientos: Array<{
          finca: string
          tipo: string
          grupo: string
          subcategoria: string
          descripcion: string
          valor: number
          fecha: string
        }>
      }>('/api/financial-manual', {
        params: {
          finca,
          tipo,
          mes: loadMonth,
          año: loadYear,
        },
      })

      const list = data?.movimientos ?? []
      if (!Array.isArray(list) || list.length === 0) {
        const emptyRows = Array.from({ length: DEFAULT_ROW_COUNT }, () => createEmptyRow())
        setRows(emptyRows)
        saveToLocalStorage(emptyRows)
        return
      }

      const mapped: RowData[] = list.map((item) => {
        const date = item.fecha as string | undefined
        const fincaApi = item.finca as string | undefined
        const tipoApi = item.tipo as string | undefined
        const category = item.grupo as string | undefined
        const subcategory = item.subcategoria as string | undefined
        const description = item.descripcion as string | undefined
        const value = item.valor as number | undefined

        const valueStr = value != null ? String(value) : ''
        const farmDisplay = fincaApi
          ? (tipoApi ? `${fincaApi}${FARM_VALUE_SEP}${tipoApi}` : `${fincaApi}${FARM_VALUE_SEP}`)
          : ''

        return {
          ...createEmptyRow(),
          date: normalizeDateForRow(date),
          farm: farmDisplay,
          category: category ?? '',
          subcategory: subcategory ?? '',
          description: description ?? '',
          value: valueStr,
        }
      })

      setRows(mapped.length > 0 ? mapped : Array.from({ length: DEFAULT_ROW_COUNT }, () => createEmptyRow()))
      saveToLocalStorage(mapped.length > 0 ? mapped : [])
      setLoadFromDbPickerOpen(false)
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : null
      setLoadFromDbError(message || 'Error al cargar movimientos desde la base de datos.')
    } finally {
      setLoadFromDbLoading(false)
    }
  }, [loadMonth, loadYear, loadFarmFilter])

  useEffect(() => {
    if (!loadFromDbPickerOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (loadFromDbPickerRef.current && !loadFromDbPickerRef.current.contains(e.target as Node)) {
        setLoadFromDbPickerOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [loadFromDbPickerOpen])

  const onFillPointerMove = useCallback((e: PointerEvent) => {
    if (!fillDragRef.current) return
    const target = document.elementFromPoint(e.clientX, e.clientY)
    const hoveredRow = target?.closest('tbody tr')
    if (!hoveredRow) return
    const rowIndex = parseInt((hoveredRow as HTMLElement).getAttribute('data-row-index') ?? '-1', 10)
    if (rowIndex < 0) return
    const start = fillDragRef.current.startRowIndex
    const end = rowIndex < start ? start : rowIndex
    fillDragRef.current.endRowIndex = end
    setFillHighlight({ start: fillDragRef.current.startRowIndex, end, col: fillDragRef.current.colIndex })
  }, [])

  const onFillPointerUp = useCallback(() => {
    window.removeEventListener('pointermove', onFillPointerMove)
    if (!fillDragRef.current || fillDragRef.current.endRowIndex <= fillDragRef.current.startRowIndex) {
      setFillHighlight(null)
      fillDragRef.current = null
      return
    }
    const { colIndex, startRowIndex, endRowIndex, value, sourceCategory } = fillDragRef.current
    setRows((prev) => {
      const next = prev.map((row, i) => {
        if (i <= startRowIndex || i > endRowIndex) return row
        const r = { ...row }
        if (colIndex === 0) r.date = value
        else if (colIndex === 1) r.farm = value
        else if (colIndex === 2) {
          r.category = value
          r.subcategory = ''
        } else if (colIndex === 3) {
          if (sourceCategory) {
            const validSubcategories = getSubcategories(sourceCategory)
            r.category = sourceCategory
            r.subcategory = validSubcategories.includes(value) ? value : ''
          }
        } else if (colIndex === 4) r.description = value
        else if (colIndex === 5) r.value = value
        return r
      })
      return next
    })
    setFillHighlight(null)
    fillDragRef.current = null
  }, [onFillPointerMove])

  const onFillPointerDown = useCallback(
    (rowIndex: number, colIndex: number) => {
      const row = rows[rowIndex]
      if (!row) return
      let value = ''
      let sourceCategory = ''
      if (colIndex === 0) value = row.date
      else if (colIndex === 1) value = row.farm
      else if (colIndex === 2) value = row.category
      else if (colIndex === 3) {
        value = row.subcategory
        sourceCategory = row.category
      } else if (colIndex === 4) value = row.description
      else if (colIndex === 5) value = row.value
      fillDragRef.current = {
        colIndex,
        startRowIndex: rowIndex,
        endRowIndex: rowIndex,
        value,
        sourceCategory,
      }
      setFillHighlight({ start: rowIndex, end: rowIndex, col: colIndex })
      window.addEventListener('pointermove', onFillPointerMove)
      window.addEventListener('pointerup', onFillPointerUp, { once: true })
    },
    [rows, onFillPointerMove, onFillPointerUp]
  )

  const isFilling = fillHighlight !== null

  return (
    <div className="movimientos-view">
      <LoadingOverlay isLoading={isSaving} />
      <main className="movimientos-view sheet-wrap">
        <header className="sheet-header">
          <h1>Registro de Movimientos</h1>
          <div className="header-actions">
            <div className="load-from-db" ref={loadFromDbPickerRef}>
              <button
                type="button"
                className="action-btn load-from-db-trigger"
                onClick={() => setLoadFromDbPickerOpen((open) => !open)}
                disabled={loadFromDbLoading}
                aria-expanded={loadFromDbPickerOpen}
                aria-haspopup="true"
              >
                Cargar desde base de datos
              </button>
              {loadFromDbPickerOpen && (
                <div
                  className="load-from-db-dropdown"
                  onMouseLeave={() => setLoadFromDbPickerOpen(false)}
                >
                  <div className="load-from-db-fields">
                    <label className="load-from-db-label">Finca</label>
                    <div className="load-from-db-select-custom">
                      <button
                        type="button"
                        className="load-from-db-select-custom-trigger"
                        onClick={() => !loadFromDbLoading && setLoadFarmPickerOpen((open) => !open)}
                        disabled={loadFromDbLoading}
                      >
                        <span className={loadFarmFilter ? '' : 'load-from-db-select-custom-placeholder'}>
                          {loadFarmFilter
                            ? (getFarmOptions(farms).find((opt) => opt.value === loadFarmFilter)?.label ?? 'Selecciona finca')
                            : 'Todas las fincas'}
                        </span>
                        <span className="load-from-db-select-custom-arrow">▾</span>
                      </button>
                      {loadFarmPickerOpen && (
                        <div className="load-from-db-select-custom-list" role="listbox">
                          <button
                            type="button"
                            className="load-from-db-select-custom-option"
                            onClick={() => {
                              setLoadFarmFilter('')
                              setLoadFarmPickerOpen(false)
                            }}
                          >
                            Todas las fincas
                          </button>
                          {getFarmOptions(farms).map((opt) => (
                            <button
                              key={opt.key}
                              type="button"
                              className="load-from-db-select-custom-option"
                              onClick={() => {
                                setLoadFarmFilter(opt.value)
                                setLoadFarmPickerOpen(false)
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <label htmlFor="movimientos-mes" className="load-from-db-label">Mes</label>
                    <select
                      id="movimientos-mes"
                      className="load-from-db-select"
                      value={loadMonth}
                      onChange={(e) => setLoadMonth(Number(e.target.value))}
                      disabled={loadFromDbLoading}
                    >
                      {MESES.map((nombre, i) => (
                        <option key={nombre} value={i + 1}>{nombre}</option>
                      ))}
                    </select>
                    <label htmlFor="movimientos-anio" className="load-from-db-label">Año</label>
                    <select
                      id="movimientos-anio"
                      className="load-from-db-select"
                      value={loadYear}
                      onChange={(e) => setLoadYear(Number(e.target.value))}
                      disabled={loadFromDbLoading}
                    >
                      {getYears().map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    className="action-btn load-from-db-btn"
                    onClick={handleLoadFromDb}
                    disabled={loadFromDbLoading}
                  >
                    {loadFromDbLoading ? 'Cargando…' : 'Cargar'}
                  </button>
                </div>
              )}
            </div>
            {loadFromDbError && (
              <span className="load-from-db-error" role="alert">{loadFromDbError}</span>
            )}
            <button type="button" className="action-btn" onClick={handleGuardar} disabled={isSaving}>
              Guardar
            </button>
            <button type="button" className="action-btn action-btn-danger" onClick={handleClearForm}>
              Limpiar formulario
            </button>
          </div>
        </header>

        <section className="sheet" aria-label="Tabla de movimientos">
          <table id="movements-table">
            <thead>
              <tr>
                <th className="row-index-header">#</th>
                <th>Fecha</th>
                <th>Finca</th>
                <th>Categoría</th>
                <th>Subcategoría</th>
                <th>Descripción</th>
                <th>Valor</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody ref={tableBodyRef} className={isFilling ? 'is-filling' : ''}>
              {rows.map((row, rowIndex) => {
                const isDateInvalid = row.date.length === 10 && !isValidDateString(row.date)
                return (
                  <tr key={row.id} data-row-index={rowIndex}>
                  <td className="row-index-cell">
                    {rowIndex + 1}
                  </td>
                  <td className={fillHighlight && rowIndex > fillHighlight.start && rowIndex <= fillHighlight.end && fillHighlight.col === 0 ? 'fill-target' : ''}>
                    <div className="date-input-wrap">
                      <input
                        type="text"
                        inputMode="numeric"
                        className={`date-input ${isDateInvalid ? 'date-input-invalid' : ''}`}
                        value={row.date}
                        onChange={(e) => updateRow(rowIndex, { date: formatDateInput(e.target.value) })}
                      />
                      {!row.date && <span className="date-input-watermark">dd/mm/aaaa</span>}
                      {isDateInvalid && (
                        <span className="date-input-error">Fecha inválida</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="fill-handle"
                      aria-label="Arrastrar para rellenar hacia abajo"
                      data-col-index="0"
                      onPointerDown={(e) => { e.preventDefault(); onFillPointerDown(rowIndex, 0); }}
                    />
                  </td>
                  <td className={fillHighlight && rowIndex > fillHighlight.start && rowIndex <= fillHighlight.end && fillHighlight.col === 1 ? 'fill-target' : ''}>
                    <div
                      className="sheet-select-custom"
                      onMouseLeave={() => {
                        if (openSheetSelect?.rowIndex === rowIndex && openSheetSelect.col === 'farm') {
                          setOpenSheetSelect(null)
                        }
                      }}
                    >
                      <button
                        type="button"
                        className={`sheet-select-custom-trigger ${row.farm ? '' : 'sheet-select-custom-placeholder'}`}
                        onClick={() =>
                          setOpenSheetSelect((current) =>
                            current && current.rowIndex === rowIndex && current.col === 'farm'
                              ? null
                              : { rowIndex, col: 'farm' }
                          )
                        }
                      >
                        <span>
                          {row.farm
                            ? (getFarmOptions(farms).find((opt) => opt.value === row.farm)?.label ?? 'Selecciona finca')
                            : 'Selecciona finca'}
                        </span>
                        <span className="sheet-select-custom-arrow">▾</span>
                      </button>
                      {openSheetSelect?.rowIndex === rowIndex && openSheetSelect.col === 'farm' && (
                        <div className="sheet-select-custom-list" role="listbox">
                          <button
                            type="button"
                            className="sheet-select-custom-option"
                            onClick={() => {
                              updateRow(rowIndex, { farm: '' })
                              setOpenSheetSelect(null)
                            }}
                          >
                            Selecciona finca
                          </button>
                          {getFarmOptions(farms).map((opt) => (
                            <button
                              key={opt.key}
                              type="button"
                              className="sheet-select-custom-option"
                              onClick={() => {
                                updateRow(rowIndex, { farm: opt.value })
                                setOpenSheetSelect(null)
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="fill-handle"
                      aria-label="Arrastrar para rellenar hacia abajo"
                      data-col-index="1"
                      onPointerDown={(e) => { e.preventDefault(); onFillPointerDown(rowIndex, 1); }}
                    />
                  </td>
                  <td className={fillHighlight && rowIndex > fillHighlight.start && rowIndex <= fillHighlight.end && fillHighlight.col === 2 ? 'fill-target' : ''}>
                    <div
                      className="sheet-select-custom"
                      onMouseLeave={() => {
                        if (openSheetSelect?.rowIndex === rowIndex && openSheetSelect.col === 'category') {
                          setOpenSheetSelect(null)
                        }
                      }}
                    >
                      <button
                        type="button"
                        className={`sheet-select-custom-trigger ${row.category ? '' : 'sheet-select-custom-placeholder'}`}
                        onClick={() =>
                          setOpenSheetSelect((current) =>
                            current && current.rowIndex === rowIndex && current.col === 'category'
                              ? null
                              : { rowIndex, col: 'category' }
                          )
                        }
                      >
                        <span>
                          {row.category || 'Selecciona categoría'}
                        </span>
                        <span className="sheet-select-custom-arrow">▾</span>
                      </button>
                      {openSheetSelect?.rowIndex === rowIndex && openSheetSelect.col === 'category' && (
                        <div className="sheet-select-custom-list" role="listbox">
                          <button
                            type="button"
                            className="sheet-select-custom-option"
                            onClick={() => {
                              updateRow(rowIndex, { category: '' })
                              setOpenSheetSelect(null)
                            }}
                          >
                            Selecciona categoría
                          </button>
                          {groups
                            .filter((g) => g.activo)
                            .sort((a, b) => a.orden - b.orden)
                            .map((g) => (
                              <button
                                key={g.nombre}
                                type="button"
                                className="sheet-select-custom-option"
                                onClick={() => {
                                  updateRow(rowIndex, { category: g.nombre })
                                  setOpenSheetSelect(null)
                                }}
                              >
                                {g.nombre}
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="fill-handle"
                      aria-label="Arrastrar para rellenar hacia abajo"
                      data-col-index="2"
                      onPointerDown={(e) => { e.preventDefault(); onFillPointerDown(rowIndex, 2); }}
                    />
                  </td>
                  <td className={fillHighlight && rowIndex > fillHighlight.start && rowIndex <= fillHighlight.end && fillHighlight.col === 3 ? 'fill-target' : ''}>
                    <div
                      className="sheet-select-custom"
                      onMouseLeave={() => {
                        if (openSheetSelect?.rowIndex === rowIndex && openSheetSelect.col === 'subcategory') {
                          setOpenSheetSelect(null)
                        }
                      }}
                    >
                      <button
                        type="button"
                        className={`sheet-select-custom-trigger ${row.subcategory ? '' : 'sheet-select-custom-placeholder'}`}
                        onClick={() =>
                          setOpenSheetSelect((current) =>
                            current && current.rowIndex === rowIndex && current.col === 'subcategory'
                              ? null
                              : { rowIndex, col: 'subcategory' }
                          )
                        }
                      >
                        <span>
                          {row.subcategory || 'Selecciona subcategoría'}
                        </span>
                        <span className="sheet-select-custom-arrow">▾</span>
                      </button>
                      {openSheetSelect?.rowIndex === rowIndex && openSheetSelect.col === 'subcategory' && (
                        <div className="sheet-select-custom-list" role="listbox">
                          <button
                            type="button"
                            className="sheet-select-custom-option"
                            onClick={() => {
                              updateRow(rowIndex, { subcategory: '' })
                              setOpenSheetSelect(null)
                            }}
                          >
                            Selecciona subcategoría
                          </button>
                          {getSubcategories(row.category).map((s) => (
                            <button
                              key={s}
                              type="button"
                              className="sheet-select-custom-option"
                              onClick={() => {
                                updateRow(rowIndex, { subcategory: s })
                                setOpenSheetSelect(null)
                              }}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="fill-handle"
                      aria-label="Arrastrar para rellenar hacia abajo"
                      data-col-index="3"
                      onPointerDown={(e) => { e.preventDefault(); onFillPointerDown(rowIndex, 3); }}
                    />
                  </td>
                  <td
                    className={
                      fillHighlight &&
                      rowIndex > fillHighlight.start &&
                      rowIndex <= fillHighlight.end &&
                      fillHighlight.col === 4
                        ? 'fill-target'
                        : ''
                    }
                  >
                    <div
                      className="description-cell"
                      onMouseEnter={() => setHoveredDescriptionRow(rowIndex)}
                      onMouseLeave={() => setHoveredDescriptionRow(null)}
                    >
                      <input
                        type="text"
                        className="description-input"
                        placeholder="Detalle"
                        value={row.description}
                        onChange={(e) => updateRow(rowIndex, { description: e.target.value })}
                        title={row.description || ''}
                      />
                      {hoveredDescriptionRow === rowIndex && row.description.trim() !== '' && (
                        <div className="description-tooltip" role="tooltip">
                          <textarea
                            className="description-tooltip-textarea"
                            value={row.description}
                            onChange={(e) => updateRow(rowIndex, { description: e.target.value })}
                            placeholder="Escribe aquí la descripción completa"
                          />
                        </div>
                      )}
                      <button
                        type="button"
                        className="fill-handle"
                        aria-label="Arrastrar para rellenar hacia abajo"
                        data-col-index="4"
                        onPointerDown={(e) => {
                          e.preventDefault()
                          onFillPointerDown(rowIndex, 4)
                        }}
                      />
                    </div>
                  </td>
                  <td className={`value-cell ${fillHighlight && rowIndex > fillHighlight.start && rowIndex <= fillHighlight.end && fillHighlight.col === 5 ? 'fill-target' : ''}`}>
                    <span className="value-currency-prefix">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="value-input"
                      placeholder="0"
                      value={formatCurrencyDisplay(row.value)}
                      onChange={(e) => updateRow(rowIndex, { value: parseCurrencyInput(e.target.value) })}
                    />
                    <button
                      type="button"
                      className="fill-handle"
                      aria-label="Arrastrar para rellenar hacia abajo"
                      data-col-index="5"
                      onPointerDown={(e) => { e.preventDefault(); onFillPointerDown(rowIndex, 5); }}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="remove-row"
                      aria-label="Eliminar fila"
                      onClick={() => removeRow(rowIndex)}
                    >
                      Eliminar
                    </button>
                  </td>
                  </tr>
                  )
                })}
            </tbody>
          </table>
        </section>

        <div className="actions-row">
          <label htmlFor="add-row-count" className="add-row-count-label">
            Filas a agregar:
            <input
              id="add-row-count"
              type="number"
              min={1}
              className="add-row-count-input"
              value={addRowCount}
              onChange={(e) => {
                const value = e.target.value
                if (value === '') {
                  setAddRowCount('')
                  return
                }
                const parsed = Number(value)
                if (!Number.isNaN(parsed) && parsed > 0) {
                  setAddRowCount(value)
                }
              }}
            />
          </label>
          <button
            type="button"
            id="add-row"
            onClick={() => {
              const count = addRowCount.trim() === '' ? 1 : Number(addRowCount)
              addRow(Number.isNaN(count) || count <= 0 ? 1 : count)
            }}
          >
            Agregar fila
          </button>
        </div>
      </main>

      {showConfirmClear && (
        <div
          className="movimientos-confirm-overlay"
          onClick={handleCancelClear}
          role="dialog"
          aria-modal="true"
          aria-labelledby="movimientos-confirm-title"
        >
          <div className="movimientos-confirm-content" onClick={(e) => e.stopPropagation()}>
            <h2 id="movimientos-confirm-title">Limpiar formulario</h2>
            <p>Esto borrará los datos del formulario actual y lo reiniciará. ¿Deseas continuar?</p>
            <div className="movimientos-confirm-actions">
              <button type="button" className="action-btn" onClick={handleCancelClear}>
                Cancelar
              </button>
              <button type="button" className="action-btn action-btn-danger" onClick={handleConfirmClear}>
                Limpiar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Movimientos
